import { memo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import type { Merchant } from "features/merchants/types";
import { BasicDialog } from "ui/molecules/Dialog";
import KYCSettingsProvider from "../KYCSettingsProvider";
import { useKYCSettingsStateSelectorsContext } from "../KYCSettingsProvider";
import useKYCActionPanel from "../KYCActionPanel/useKYCActionPanel";
import Services from "../Services";
import IncludeMethods from "../IncludeMethods";

type Props = {
    readonly merchant: Merchant | null;
    readonly open: boolean;
    readonly onClose: () => void;
    readonly onSaved: () => void;
};

type DialogBodyProps = {
    readonly merchant: Merchant;
    readonly onClose: () => void;
    readonly onSaved: () => void;
};

const DialogBody = ({ merchant, onClose, onSaved }: DialogBodyProps) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const { isLoading, isDisabled, onSave } = useKYCActionPanel();
    const { selectIsKYCSettingsChanged } = useKYCSettingsStateSelectorsContext();

    const handleSave = async () => {
        await onSave();
        setIsConfirmOpen(false);
        onSaved();
    };

    const handleCloseRequest = () => {
        if (isLoading) return;
        onClose();
    };

    const closeConfirm = () => {
        if (isLoading) return;
        setIsConfirmOpen(false);
    };

    return (
        <>
            <BasicDialog
                isOpen
                onClose={handleCloseRequest}
                isCanClose={!isLoading && !isConfirmOpen}
                titleSlot={(
                    <Typography variant='h6' sx={{ fontWeight: 700 }}>
                        KYC Settings — {merchant.fullName}
                    </Typography>
                )}
                actionsSlot={(
                    <Stack direction='row' spacing={2}>
                        <Button
                            variant='outlined'
                            onClick={onClose}
                            disabled={isLoading}
                            data-test-id='merchantKycSettings.edit.close'
                        >
                            Close
                        </Button>
                        <Button
                            variant='contained'
                            onClick={() => setIsConfirmOpen(true)}
                            disabled={isDisabled}
                            data-test-id='merchantKycSettings.edit.save'
                        >
                            Save Changes
                        </Button>
                    </Stack>
                )}
            >
                <Box sx={{ width: 960, maxWidth: '100%' }}>
                    <Services />
                    <IncludeMethods />
                </Box>
            </BasicDialog>

            <BasicDialog
                isOpen={isConfirmOpen}
                onClose={closeConfirm}
                isCanClose={!isLoading}
                actionsSlot={(
                    <Stack direction='row' spacing={2}>
                        <LoadingButton
                            variant='contained'
                            loading={isLoading}
                            onPointerDown={handleSave}
                            disabled={!selectIsKYCSettingsChanged()}
                            data-test-id='merchantKycSettings.edit.save.confirm'
                        >
                            Yes, save changes
                        </LoadingButton>
                        <Button
                            variant='outlined'
                            onPointerDown={closeConfirm}
                            disabled={isLoading}
                            data-test-id='merchantKycSettings.edit.save.cancel'
                        >
                            Cancel
                        </Button>
                    </Stack>
                )}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography
                        variant='h6'
                        sx={{ fontWeight: 700, textAlign: 'center' }}
                    >
                        Are you sure you want to save KYC settings for this merchant?
                    </Typography>
                    <Typography
                        variant='body1'
                        sx={{ mt: 2, textAlign: 'center' }}
                    >
                        “{merchant.fullName}”
                    </Typography>
                </Box>
            </BasicDialog>
        </>
    );
};

const EditMerchantKycDialog = ({ merchant, open, onClose, onSaved }: Props) => {
    if (!open || !merchant) {
        return null;
    }

    return (
        <KYCSettingsProvider merchantId={merchant.coreId}>
            <DialogBody
                merchant={merchant}
                onClose={onClose}
                onSaved={onSaved}
            />
        </KYCSettingsProvider>
    );
};

export default memo(EditMerchantKycDialog);
